// src/pages/RoleManagement.js
import React, { useState, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider'; // used in left nav panel
import { confirmDialog } from 'primereact/confirmdialog';

// ── Helpers ───────────────────────────────────────────────────────────────────

const flatPermIds = (permsByCategory) => {
    const ids = new Set();
    if (!permsByCategory) return ids;
    Object.values(permsByCategory).forEach(perms =>
        perms.forEach(p => ids.add(p.id))
    );
    return ids;
};

const PERM_ORDER = ['view_', 'create_', 'manage_', 'approve_', 'download_', 'record_', 'inventory_bulk'];

const sortPermissions = (permissions) => {
    return [...permissions].sort((a, b) => {
        const getBase   = (c) => c.replace(/^(view_|manage_|create_|approve_|download_|record_|inventory_bulk_?)/, '');
        const getPrefix = (c) => { const i = PERM_ORDER.findIndex(p => c.startsWith(p)); return i === -1 ? 99 : i; };
        const baseA = getBase(a.codename), baseB = getBase(b.codename);
        if (baseA !== baseB) return baseA.localeCompare(baseB);
        return getPrefix(a.codename) - getPrefix(b.codename);
    });
};

// ── Component ─────────────────────────────────────────────────────────────────

const RoleManagement = () => {
    const [roles, setRoles]                   = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [selectedRole, setSelectedRole]     = useState(null);
    const [selectedPerms, setSelectedPerms]   = useState(new Set());
    const [members, setMembers]               = useState([]);
    const [membersVisible, setMembersVisible] = useState(false);
    const [editName, setEditName]             = useState('');
    const [editDesc, setEditDesc]             = useState('');
    const [loading, setLoading]               = useState(true);
    const [saving, setSaving]                 = useState(false);
    const [isNew, setIsNew]                   = useState(false);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const [rolesRes, permsRes] = await Promise.all([
                    axiosInstance.get('/api/roles/'),
                    axiosInstance.get('/api/permissions/'),
                ]);
                setRoles(rolesRes.data);
                setAllPermissions(permsRes.data);
            } catch (err) {
                handleApiError(err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const handleSelectRole = (role) => {
        setIsNew(false);
        setSelectedRole(role);
        setEditName(role.name);
        setEditDesc(role.description || '');
        setSelectedPerms(flatPermIds(role.permissions_by_category));
        setMembers([]);
    };

    const handleNewRole = () => {
        setIsNew(true);
        setSelectedRole(null);
        setEditName('');
        setEditDesc('');
        setSelectedPerms(new Set());
        setMembers([]);
    };

    const togglePerm = (permId) => {
        setSelectedPerms(prev => {
            const next = new Set(prev);
            next.has(permId) ? next.delete(permId) : next.add(permId);
            return next;
        });
    };

    const toggleCategory = (categoryPerms) => {
        const allSelected = categoryPerms.every(p => selectedPerms.has(p.id));
        setSelectedPerms(prev => {
            const next = new Set(prev);
            if (allSelected) { categoryPerms.forEach(p => next.delete(p.id)); }
            else             { categoryPerms.forEach(p => next.add(p.id)); }
            return next;
        });
    };

    const handleSave = async () => {
        if (!editName.trim()) { handleApiError({ detail: 'Permission name is required.' }); return; }
        setSaving(true);
        try {
            const payload = { name: editName.trim(), description: editDesc.trim(), permissions: [...selectedPerms] };
            if (isNew) {
                const res = await axiosInstance.post('/api/roles/', payload);
                setRoles(prev => [...prev, res.data]);
                setSelectedRole(res.data);
                setIsNew(false);
                handleApiSuccess(`Permission "${res.data.name}" created successfully!`);
            } else {
                const res = await axiosInstance.put(`/api/roles/${selectedRole.id}/`, payload);
                setRoles(prev => prev.map(r => r.id === res.data.id ? res.data : r));
                setSelectedRole(res.data);
                setSelectedPerms(flatPermIds(res.data.permissions_by_category));
                handleApiSuccess(`Permission "${res.data.name}" updated successfully!`);
            }
        } catch (err) {
            handleApiError(err);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        confirmDialog({
            message: `Delete permission "${selectedRole.name}"? This will remove it from all assigned users.`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axiosInstance.delete(`/api/roles/${selectedRole.id}/`);
                    setRoles(prev => prev.filter(r => r.id !== selectedRole.id));
                    setSelectedRole(null);
                    setIsNew(false);
                    handleApiSuccess('Permission deleted.');
                } catch (err) { handleApiError(err); }
            },
        });
    };

    const handleViewMembers = async () => {
        try {
            const res = await axiosInstance.get(`/api/roles/${selectedRole.id}/members/`);
            setMembers(res.data);
            setMembersVisible(true);
        } catch (err) { handleApiError(err); }
    };

    const handleSelectAll = () => {
        const all = new Set();
        allPermissions.forEach(cat => cat.permissions.forEach(p => all.add(p.id)));
        setSelectedPerms(all);
    };
    const handleSelectNone = () => setSelectedPerms(new Set());

    const totalPerms = allPermissions.reduce((n, cat) => n + cat.permissions.length, 0);
    const isEditing  = isNew || selectedRole;

    return (
        <div className="grid">
            {/* ── Left nav panel — matches OrganizationSettingsPage exactly ── */}
            <div className="col-fixed" style={{ width: '220px' }}>
                <div className="p-3 surface-card border-round shadow-1" style={{ position: 'sticky', top: '1rem' }}>
                    <h6 className="mb-3">Permissions</h6>

                    {/* Role list as nav buttons */}
                    {loading ? (
                        <p className="text-color-secondary" style={{ fontSize: '0.875rem' }}>Loading…</p>
                    ) : roles.length === 0 ? (
                        <p className="text-color-secondary" style={{ fontSize: '0.875rem' }}>
                            No permissions yet.
                        </p>
                    ) : (
                        roles.map(role => (
                            <Button
                                key={role.id}
                                label={role.name}
                                icon={selectedRole?.id === role.id && !isNew ? 'pi pi-chevron-right' : 'pi pi-shield'}
                                className={`p-button-text w-full text-left mb-1 ${selectedRole?.id === role.id && !isNew ? 'p-button-primary' : ''}`}
                                onClick={() => handleSelectRole(role)}
                            />
                        ))
                    )}

                    <Divider />

                    <Button
                        icon="pi pi-plus"
                        label="New Permission"
                        className="p-button-outlined w-full mb-3"
                        onClick={handleNewRole}
                    />

                    {/* Save button — only shown when editing */}
                    {isEditing && (
                        <Button
                            label={isNew ? 'Create Permission' : 'Save Changes'}
                            icon="pi pi-save"
                            className="p-button-primary w-full"
                            loading={saving}
                            onClick={handleSave}
                        />
                    )}
                </div>
            </div>

            {/* ── Right content panel ────────────────────────────────────── */}
            <div className="col">
                    <h3 className="mb-4">Permission Management</h3>

                    {!isEditing ? (
                        /* Placeholder */
                        <div className="surface-card p-4 border-round shadow-1 mb-4"
                             style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                            <i className="pi pi-shield mb-3"
                               style={{ fontSize: '2.5rem', color: '#d1d5db', display: 'block' }} />
                            <p className="text-color-secondary">
                                Select a permission from the left panel, or click <strong>New Permission</strong> to create one.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* ── Role details section ── */}
                            <section className="surface-card p-4 border-round shadow-1 mb-4">
                                <div className="flex align-items-center justify-content-between mb-3">
                                    <h5 className="m-0">
                                        {isNew ? 'New Permission' : `Edit: ${selectedRole.name}`}
                                    </h5>
                                    {!isNew && (
                                        <div className="flex gap-2">
                                            <Button
                                                label="Members"
                                                icon="pi pi-users"
                                                className="p-button-outlined p-button-sm"
                                                onClick={handleViewMembers}
                                            />
                                            <Button
                                                label="Delete"
                                                icon="pi pi-trash"
                                                className="p-button-danger p-button-outlined p-button-sm"
                                                onClick={handleDelete}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="formgrid grid">
                                    <div className="field col-12 md:col-4">
                                        <label htmlFor="roleName" className="font-bold">Permission Name *</label>
                                        <InputText
                                            id="roleName"
                                            value={editName}
                                            onChange={e => setEditName(e.target.value)}
                                            placeholder="e.g. Pharmacist, Cashier"
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="field col-12 md:col-8">
                                        <label htmlFor="roleDesc" className="font-bold">Description</label>
                                        <InputTextarea
                                            id="roleDesc"
                                            value={editDesc}
                                            onChange={e => setEditDesc(e.target.value)}
                                            placeholder="Optional — describe what this role can do"
                                            rows={1}
                                            autoResize
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* ── Permissions section ── */}
                            <section className="surface-card p-4 border-round shadow-1 mb-4">
                                <div className="flex align-items-center justify-content-between mb-3">
                                    <div className="flex align-items-center gap-2">
                                        <h5 className="m-0">Permissions</h5>
                                        <span style={{
                                            display: 'inline-block',
                                            background: '#dbeafe',
                                            color: '#1e40af',
                                            borderRadius: '9999px',
                                            padding: '1px 10px',
                                            fontSize: '0.78rem',
                                            fontWeight: 600,
                                        }}>
                                            {selectedPerms.size} / {totalPerms}
                                        </span>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button label="Select All" className="p-button-text p-button-sm" onClick={handleSelectAll} />
                                        <Button label="Clear All"  className="p-button-text p-button-sm" onClick={handleSelectNone} />
                                    </div>
                                </div>

                                {(() => {
                                    // Pin Customers first, then remaining categories in original order
                                    const sorted = [
                                        ...allPermissions.filter(c => c.category.toLowerCase() === 'customers'),
                                        ...allPermissions.filter(c => c.category.toLowerCase() !== 'customers'),
                                    ];

                                    // Chunk into rows of 3
                                    const rows = [];
                                    for (let i = 0; i < sorted.length; i += 3) {
                                        rows.push(sorted.slice(i, i + 3));
                                    }

                                    const renderCategory = ({ category, permissions }) => {
                                        const sortedPerms     = sortPermissions(permissions);
                                        const allCatSelected  = sortedPerms.every(p => selectedPerms.has(p.id));
                                        const someCatSelected = sortedPerms.some(p => selectedPerms.has(p.id));

                                        return (
                                            <div key={category}>
                                                {/* Category header */}
                                                <div className="flex align-items-center gap-2 mb-2">
                                                    <Checkbox
                                                        inputId={`cat-${category}`}
                                                        checked={allCatSelected}
                                                        onChange={() => toggleCategory(sortedPerms)}
                                                        style={someCatSelected && !allCatSelected ? { opacity: 0.6 } : {}}
                                                    />
                                                    <label
                                                        htmlFor={`cat-${category}`}
                                                        className="font-bold"
                                                        style={{ cursor: 'pointer', textTransform: 'uppercase', fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.05em' }}
                                                    >
                                                        {category}
                                                    </label>
                                                </div>

                                                {/* Permission checkboxes — stacked vertically within each cell */}
                                                <div className="flex flex-column gap-2 pl-3">
                                                    {sortedPerms.map(perm => (
                                                        <div key={perm.id} className="flex align-items-center gap-2">
                                                            <Checkbox
                                                                inputId={`perm-${perm.id}`}
                                                                checked={selectedPerms.has(perm.id)}
                                                                onChange={() => togglePerm(perm.id)}
                                                            />
                                                            <label
                                                                htmlFor={`perm-${perm.id}`}
                                                                style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                                                                title={perm.codename}
                                                            >
                                                                {perm.label}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    };

                                    return (
                                        <>
                                            {rows.map((row, rowIdx) => (
                                                <React.Fragment key={rowIdx}>
                                                    {/* Divider between rows, not before the first */}
                                                    {rowIdx > 0 && <Divider className="my-3" />}

                                                    {/* 3-column grid for this row */}
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 2rem' }}>
                                                        {row.map((cat, colIdx) => (
                                                            <div
                                                                key={cat.category}
                                                                style={colIdx > 0 ? { borderLeft: '1px solid var(--surface-border)', paddingLeft: '2rem' } : {}}
                                                            >
                                                                {renderCategory(cat)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </React.Fragment>
                                            ))}
                                        </>
                                    );
                                })()}
                            </section>
                        </>
                    )}
                
            </div>

            {/* ── Members dialog ────────────────────────────────────────────── */}
            <Dialog
                header={`Members — ${selectedRole?.name}`}
                visible={membersVisible}
                onHide={() => setMembersVisible(false)}
                style={{ width: '28rem' }}
                modal
            >
                {members.length === 0 ? (
                    <p className="text-color-secondary text-center p-3">
                        No users have this role assigned yet.
                    </p>
                ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {members.map(m => (
                            <li key={m.employee_id}
                                className="flex align-items-center gap-3 py-2"
                                style={{ borderBottom: '1px solid var(--surface-border)' }}>
                                <i className="pi pi-user text-color-secondary" />
                                <div>
                                    <div className="font-bold" style={{ fontSize: '0.9rem' }}>
                                        {m.full_name || m.username}
                                    </div>
                                    <small className="text-color-secondary">@{m.username}</small>
                                </div>
                                <small className="text-color-secondary ml-auto">
                                    {m.assigned_at ? new Date(m.assigned_at).toLocaleDateString() : ''}
                                </small>
                            </li>
                        ))}
                    </ul>
                )}
            </Dialog>
        </div>
    );
};

export default RoleManagement;